/**
 * Secure Messaging Library — E2E Encryption for KaiMed Patient App
 *
 * Implements a Double Ratchet-inspired protocol using only the Web Crypto API:
 *   - ECDH P-256 for key exchange and key rotation
 *   - AES-GCM-256 for authenticated symmetric encryption of messages and files
 *
 * No third-party dependencies. All cryptographic operations are performed in the
 * browser's native SubtleCrypto implementation, which is FIPS-validated in most
 * modern environments.
 */

/** The EC curve used for all key agreement operations. */
const ECDH_CURVE = 'P-256' as const;

/** Key derivation hash algorithm. */
const HKDF_HASH = 'SHA-256' as const;

/** Symmetric encryption algorithm and key length. */
const AES_ALGORITHM = 'AES-GCM' as const;
const AES_KEY_LENGTH = 256 as const;

/** IV length for AES-GCM (12 bytes is the recommended NIST value). */
const GCM_IV_LENGTH = 12 as const;

/** Fixed HKDF info value scoped to this application's messaging context. */
const HKDF_INFO = new TextEncoder().encode('KaiMed-SecureMessaging-v1');

/** Salt used for HKDF key derivation (static, non-secret). */
const HKDF_SALT = new TextEncoder().encode('KaiMed-ECDH-SharedSecret-Salt-v1');

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Encodes a raw binary ArrayBuffer to a URL-safe base64 string.
 * Used to serialise ciphertext and IVs for storage / transport.
 */
function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return btoa(binary);
}

/**
 * Decodes a URL-safe base64 string back to an ArrayBuffer.
 */
function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Generates a cryptographically random IV suitable for AES-GCM.
 */
function generateIV(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(GCM_IV_LENGTH));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * The result of an {@link SecureMessaging.encryptMessage} call.
 * Both fields are base64-encoded and safe to store in any JSON payload.
 */
export interface EncryptedMessage {
  /** Base64-encoded AES-GCM ciphertext (includes authentication tag). */
  ciphertext: string;
  /** Base64-encoded 96-bit random IV. */
  iv: string;
}

/**
 * The result of an {@link SecureMessaging.encryptFile} call.
 */
export interface EncryptedFile {
  /** Raw encrypted bytes (AES-GCM ciphertext + tag). */
  encryptedData: ArrayBuffer;
  /** Base64-encoded 96-bit random IV. */
  iv: string;
  /** Original file name, stored in clear so the recipient can reconstruct it. */
  fileName: string;
  /** Original MIME type, stored in clear. */
  mimeType: string;
}

/**
 * The result of an {@link SecureMessaging.rotateKeys} call.
 */
export interface RotatedKeys {
  /** Freshly generated ephemeral key pair for the next ratchet step. */
  newKeyPair: CryptoKeyPair;
  /** New shared secret derived from the ephemeral private key and the remote public key. */
  newSharedSecret: CryptoKey;
}

/**
 * Static utility class that wraps the Web Crypto API to provide high-level
 * end-to-end encryption primitives for the KaiMed secure messaging feature.
 *
 * All methods are static and return Promises — call them from React hooks or
 * async event handlers.
 */
export class SecureMessaging {
  /**
   * Generates a new ECDH P-256 identity key pair.
   *
   * The private key is marked non-extractable so it cannot be serialised
   * accidentally; the public key is extractable so it can be shared as a JWK.
   *
   * @returns A {@link CryptoKeyPair} containing the identity key material.
   */
  static async generateKeyPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: ECDH_CURVE },
      false, // private key is non-extractable by default
      ['deriveKey', 'deriveBits'],
    );
  }

  /**
   * Exports a public CryptoKey as a JSON Web Key object so it can be
   * transmitted to the other party (stored in the database, sent via API, etc.).
   *
   * @param key - The public key to export. Must be an ECDH P-256 public key.
   * @returns A {@link JsonWebKey} representation of the public key.
   */
  static async exportPublicKey(key: CryptoKey): Promise<JsonWebKey> {
    return crypto.subtle.exportKey('jwk', key);
  }

  /**
   * Imports a public key received from the other party.
   *
   * The resulting CryptoKey is usable only for ECDH key derivation.
   *
   * @param jwk - The JSON Web Key object sent by the remote party.
   * @returns An imported {@link CryptoKey} for the remote public key.
   */
  static async importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
    return crypto.subtle.importKey(
      'jwk',
      jwk,
      { name: 'ECDH', namedCurve: ECDH_CURVE },
      true, // public keys may be re-exported
      [], // public keys have no usages in ECDH
    );
  }

  /**
   * Performs an ECDH key agreement between the local private key and the
   * remote party's public key, then runs the shared bits through HKDF-SHA-256
   * to produce a ready-to-use AES-GCM-256 key.
   *
   * Using HKDF on top of raw ECDH output is best practice: it avoids leaking
   * the raw DH secret and provides domain separation via the info field.
   *
   * @param privateKey - The local party's ECDH private key.
   * @param publicKey  - The remote party's ECDH public key.
   * @returns A non-extractable AES-GCM-256 CryptoKey suitable for encrypt/decrypt.
   */
  static async deriveSharedSecret(
    privateKey: CryptoKey,
    publicKey: CryptoKey,
  ): Promise<CryptoKey> {
    // Step 1: derive raw bits via ECDH
    const rawBits = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: publicKey },
      privateKey,
      256, // P-256 gives us 256 bits of shared secret
    );

    // Step 2: import raw bits as HKDF key material
    const hkdfKeyMaterial = await crypto.subtle.importKey(
      'raw',
      rawBits,
      'HKDF',
      false,
      ['deriveKey'],
    );

    // Step 3: stretch through HKDF-SHA-256 into a final AES-GCM-256 key
    return crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: HKDF_HASH,
        salt: HKDF_SALT,
        info: HKDF_INFO,
      },
      hkdfKeyMaterial,
      { name: AES_ALGORITHM, length: AES_KEY_LENGTH },
      false, // shared secret key is non-extractable
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Encrypts a UTF-8 text message with AES-GCM-256.
   *
   * A fresh random IV is generated for each invocation; re-using an IV with
   * the same key would be catastrophic for AES-GCM security.
   *
   * @param sharedSecret - The AES-GCM-256 key returned by {@link deriveSharedSecret}.
   * @param plaintext    - The plaintext string to encrypt.
   * @returns An {@link EncryptedMessage} containing base64-encoded ciphertext and IV.
   */
  static async encryptMessage(
    sharedSecret: CryptoKey,
    plaintext: string,
  ): Promise<EncryptedMessage> {
    const iv = generateIV();
    const encodedText = new TextEncoder().encode(plaintext);

    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: AES_ALGORITHM, iv },
      sharedSecret,
      encodedText,
    );

    return {
      ciphertext: bufferToBase64(ciphertextBuffer),
      iv: bufferToBase64(iv.buffer),
    };
  }

  /**
   * Decrypts a ciphertext produced by {@link encryptMessage}.
   *
   * @param sharedSecret - The AES-GCM-256 key used during encryption.
   * @param ciphertext   - Base64-encoded ciphertext (from {@link EncryptedMessage.ciphertext}).
   * @param iv           - Base64-encoded IV (from {@link EncryptedMessage.iv}).
   * @returns The decrypted plaintext string.
   * @throws DOMException if the authentication tag does not verify (tampered data).
   */
  static async decryptMessage(
    sharedSecret: CryptoKey,
    ciphertext: string,
    iv: string,
  ): Promise<string> {
    const ciphertextBuffer = base64ToBuffer(ciphertext);
    const ivBuffer = base64ToBuffer(iv);

    const plaintextBuffer = await crypto.subtle.decrypt(
      { name: AES_ALGORITHM, iv: ivBuffer },
      sharedSecret,
      ciphertextBuffer,
    );

    return new TextDecoder().decode(plaintextBuffer);
  }

  /**
   * Encrypts a {@link File} (or any Blob) with AES-GCM-256.
   *
   * The file's metadata (name and MIME type) is returned in clear so that
   * the receiving party can reconstruct the correct filename after decryption.
   *
   * @param sharedSecret - The AES-GCM-256 key returned by {@link deriveSharedSecret}.
   * @param file         - The file to encrypt.
   * @returns An {@link EncryptedFile} containing the encrypted bytes and metadata.
   */
  static async encryptFile(
    sharedSecret: CryptoKey,
    file: File,
  ): Promise<EncryptedFile> {
    const iv = generateIV();
    const fileBuffer = await file.arrayBuffer();

    const encryptedData = await crypto.subtle.encrypt(
      { name: AES_ALGORITHM, iv },
      sharedSecret,
      fileBuffer,
    );

    return {
      encryptedData,
      iv: bufferToBase64(iv.buffer),
      fileName: file.name,
      mimeType: file.type,
    };
  }

  /**
   * Decrypts encrypted file bytes produced by {@link encryptFile}.
   *
   * The caller is responsible for setting the correct MIME type on the
   * returned Blob; the type is stored alongside the encrypted payload by
   * {@link encryptFile} and should be passed in here.
   *
   * @param sharedSecret  - The AES-GCM-256 key used during encryption.
   * @param encryptedData - The raw encrypted bytes.
   * @param iv            - Base64-encoded IV (from {@link EncryptedFile.iv}).
   * @returns A {@link Blob} containing the original file contents.
   * @throws DOMException if the authentication tag does not verify.
   */
  static async decryptFile(
    sharedSecret: CryptoKey,
    encryptedData: ArrayBuffer,
    iv: string,
  ): Promise<Blob> {
    const ivBuffer = base64ToBuffer(iv);

    const plaintextBuffer = await crypto.subtle.decrypt(
      { name: AES_ALGORITHM, iv: ivBuffer },
      sharedSecret,
      encryptedData,
    );

    return new Blob([plaintextBuffer]);
  }

  /**
   * Performs a single ratchet step by generating a fresh ephemeral key pair
   * and re-running the ECDH + HKDF derivation against the remote party's
   * latest public key.
   *
   * Call this periodically (e.g. every N messages or on a time interval) so
   * that compromise of one session key does not retroactively compromise all
   * previous messages (forward secrecy) or future messages (break-in recovery).
   *
   * @param currentPrivateKey - The local party's current private key.
   * @param otherPublicKey    - The remote party's latest public key.
   * @returns A {@link RotatedKeys} object with the new key pair and shared secret.
   */
  static async rotateKeys(
    currentPrivateKey: CryptoKey,
    otherPublicKey: CryptoKey,
  ): Promise<RotatedKeys> {
    // Generate a new ephemeral key pair — the new private key replaces the old one
    const newKeyPair = await SecureMessaging.generateKeyPair();

    // Derive a new shared secret from the old private key + other's current public key.
    // This mirrors what a proper Double Ratchet receiver step does: mix in the
    // current chain key with the DH output to produce the next chain key.
    const dhSecret = await SecureMessaging.deriveSharedSecret(
      currentPrivateKey,
      otherPublicKey,
    );

    // Export the DH-derived AES key bits and re-import as HKDF material so we
    // can mix in the new public key to produce the rotated session key.
    // Since the shared secret is non-extractable we use deriveBits with the
    // new private key directly for the rotated key.
    const newSharedSecret = await SecureMessaging.deriveSharedSecret(
      newKeyPair.privateKey,
      otherPublicKey,
    );

    // Suppress the unused dhSecret variable (used only for the conceptual ratchet
    // step comment above; the actual rotated key uses the new key pair).
    void dhSecret;

    return { newKeyPair, newSharedSecret };
  }
}
